/**
 * Responsibility: Implement atlas graph analysis behavior for the engine layer.
 * Scope: Owns deterministic atlas topology helpers only; rendering stays elsewhere.
 */
import type { Atlas, AtlasEdge } from "@anyquest/shared-core"
import { parseLocKey } from './state'

export type AtlasDirection = 'north' | 'east' | 'south' | 'west' | 'up' | 'down'

export interface AtlasNeighbor {
  key: string
  x: number
  y: number
  z: number
  direction: AtlasDirection
}

export type AdjacencyList = Record<string, AtlasNeighbor[]>

export interface AtlasComponentReport {
  startKey: string
  reachableKeys: string[]
  frontierKeys: string[]
  loopKeys: string[]
  degrees: Record<string, number>
  distances: Record<string, number>
  suggestedExpansion:
    | {
        fromKey: string
        toKey: string
        direction: AtlasDirection
      }
    | null
}

export const DIRECTION_SPECS: Array<{ direction: AtlasDirection; x: number; y: number; z: number }> = [
  { direction: 'north', x: 0, y: 1, z: 0 },
  { direction: 'east', x: 1, y: 0, z: 0 },
  { direction: 'south', x: 0, y: -1, z: 0 },
  { direction: 'west', x: -1, y: 0, z: 0 },
  { direction: 'up', x: 0, y: 0, z: 1 },
  { direction: 'down', x: 0, y: 0, z: -1 },
]

export function buildAdjacencyList(atlas: Atlas | null | undefined): AdjacencyList {
  const adj: AdjacencyList = {}
  if (!atlas) return adj

  for (const edge of atlas.edges ?? []) {
    const x1 = edge.x1, y1 = edge.y1, z1 = edge.z1 ?? 0
    const x2 = edge.x2, y2 = edge.y2, z2 = edge.z2 ?? 0
    const k1 = `${x1}_${y1}_${z1}`
    const k2 = `${x2}_${y2}_${z2}`

    if (!adj[k1]) adj[k1] = []
    if (!adj[k2]) adj[k2] = []

    const dir1 = resolveDirection(x1, y1, z1, x2, y2, z2)
    const dir2 = resolveDirection(x2, y2, z2, x1, y1, z1)

    if (dir1) adj[k1].push({ key: k2, x: x2, y: y2, z: z2, direction: dir1 })
    if (dir2) adj[k2].push({ key: k1, x: x1, y: y1, z: z1, direction: dir2 })
  }
  return adj
}

function resolveDirection(x1: number, y1: number, z1: number, x2: number, y2: number, z2: number): AtlasDirection | null {
  const dx = x2 - x1
  const dy = y2 - y1
  const dz = z2 - z1
  return DIRECTION_SPECS.find(s => s.x === dx && s.y === dy && s.z === dz)?.direction ?? null
}

export function atlasDegrees(atlas: Atlas | null | undefined): Record<string, number> {
  const adj = buildAdjacencyList(atlas)
  const degrees: Record<string, number> = {}
  for (const key of Object.keys(atlas?.nodes ?? {})) {
    degrees[key] = adj[key]?.length ?? 0
  }
  return degrees
}

export function atlasNeighbors(atlas: Atlas | null | undefined, key: string): AtlasNeighbor[] {
  if (!atlas) return []
  // For single lookups, we can't afford full build, but internal build is $O(E)$.
  // Optimization: use the adjacency list if calling in a loop.
  // Legacy support for $O(E)$ direct call:
  return atlasNeighborsFast(buildAdjacencyList(atlas), key)
}

export function atlasNeighborsFast(adj: AdjacencyList, key: string): AtlasNeighbor[] {
  return adj[key] ?? []
}

export function findEdge(atlas: Atlas, x1: number, y1: number, z1: number, x2: number, y2: number, z2: number): AtlasEdge | null {
  return (atlas.edges ?? []).find(e => {
    const ez1 = e.z1 ?? 0
    const ez2 = e.z2 ?? 0
    return ((e.x1 === x1 && e.y1 === y1 && ez1 === z1 && e.x2 === x2 && e.y2 === y2 && ez2 === z2) ||
            (e.x1 === x2 && e.y1 === y2 && ez1 === z2 && e.x2 === x1 && e.y2 === y1 && ez2 === z1))
  }) ?? null
}

export function findEdgeFast(atlas: Atlas, k1: string, k2: string): AtlasEdge | null {
  const p1 = parseLocKey(k1)
  const p2 = parseLocKey(k2)
  if (!p1 || !p2) return null
  return findEdge(atlas, p1[0], p1[1], p1[2], p2[0], p2[1], p2[2])
}

function expansionCandidate(atlas: Atlas, key: string, adj: AdjacencyList) {
  const parsed = parseLocKey(key)
  if (!parsed) return null
  const occupied = new Set((adj[key] ?? []).map((entry) => entry.direction))
  
  // SPATIAL-BUG-03: Prioritize verticality sometimes to avoid flat world generation
  // We alternate priority or specifically check vertical first if horizontal is somewhat crowded
  const verticalFirst = (parsed[0] + parsed[1]) % 2 === 0
  const specs = [...DIRECTION_SPECS]
  if (verticalFirst) {
    // Move Up/Down to the front
    const up = specs.splice(4, 1)[0]
    const down = specs.splice(4, 1)[0]
    specs.unshift(up, down)
  }

  const direction = specs.find((spec) => !occupied.has(spec.direction))
  if (!direction) return null
  return {
    fromKey: key,
    toKey: `${parsed[0] + direction.x}_${parsed[1] + direction.y}_${parsed[2] + direction.z}`,
    direction: direction.direction,
  }
}

export function buildAtlasComponentReport(atlas: Atlas | null | undefined, startKey: string): AtlasComponentReport {
  const safeAtlas = atlas ?? { nodes: {}, edges: [] }
  const adj = buildAdjacencyList(safeAtlas)
  const reachable = new Set<string>()
  const distances: Record<string, number> = {}
  const queue: string[] = []

  if (safeAtlas.nodes[startKey]) {
    reachable.add(startKey)
    distances[startKey] = 0
    queue.push(startKey)
  }

  while (queue.length) {
    const current = queue.shift()!
    for (const neighbor of (adj[current] ?? [])) {
      if (reachable.has(neighbor.key) || !safeAtlas.nodes[neighbor.key]) continue
      reachable.add(neighbor.key)
      distances[neighbor.key] = (distances[current] ?? 0) + 1
      queue.push(neighbor.key)
    }
  }

  const reachableKeys = [...reachable]
  const degrees: Record<string, number> = {}
  for (const key of reachableKeys) {
    degrees[key] = adj[key]?.length ?? 0
  }

  const frontierKeys = reachableKeys.filter((key) => (degrees[key] ?? 0) <= 1)
  const loopKeys = reachableKeys.filter((key) => (degrees[key] ?? 0) >= 2)

  const suggestedExpansion = reachableKeys
    .slice()
    .sort((left, right) => {
      const distanceDelta = (distances[right] ?? 0) - (distances[left] ?? 0)
      if (distanceDelta) return distanceDelta
      return left.localeCompare(right)
    })
    .map((key) => expansionCandidate(safeAtlas, key, adj))
    .find(Boolean) ?? null

  return {
    startKey,
    reachableKeys,
    frontierKeys,
    loopKeys,
    degrees,
    distances,
    suggestedExpansion,
  }
}
